'use client'

import {
  ISbStoriesParams,
  SbBlokData,
  storyblokEditable,
  StoryblokServerComponent,
} from '@storyblok/react/rsc'
import { FC, useEffect, useState } from 'react'
import StoryblokClient from 'storyblok-js-client'

import { PhnxImageHolder } from '@/components/01-atoms/ImageHolder/ImageHolder'
import { PhnxAuthorcard } from '@/components/02-molecules/Authorcard/Authorcard'
import { PhnxCard } from '@/components/02-molecules/Card/Card'
import { getStoryblokApi } from '@/lib/storyblok'
import { stylePanelSBData } from '@/lib/stylePanel'
const Storyblok = new StoryblokClient({ accessToken: process.env.NEXT_PUBLIC_SB_PREVIEW_TOKEN })

import { PhnxButton } from '@/components/01-atoms/Button/Button'
import { setDate } from '@/lib/date'
import { calculateReadingTime } from '@/lib/readingTime'
import { searchParamsData } from '@/lib/sbAPI'

import { PhnxRelatedArticles } from './RelatedArticles'
import { RelatedArticlesProps } from './RelatedArticles'

interface RelatedarticlesBlokProps {
  blok: {
    title?: RelatedArticlesProps['title']
    subtitle?: RelatedArticlesProps['subtitle']
    button?: SbBlokData[]
    items: any
    manualItems?: any
    description?: SbBlokData[]
    layout: RelatedArticlesProps['layout']
    padding?: SbBlokData[]
    background?: SbBlokData[]
    colour?: SbBlokData[]
    card_option?: string
    pullItemsByType: any
  } & SbBlokData
  rels: any
}

function ArticleReadTime(body: any) {
  if (!body || !body[0]?.content) return '0 min read'
  const html = Storyblok.richTextResolver.render(body[0].content)
  return calculateReadingTime(html)
}

const Relatedarticles: FC<RelatedarticlesBlokProps> = ({ blok, rels }) => {
  const stylePanel = stylePanelSBData(blok.colour, blok.background, undefined, blok.padding)
  const [cardItems, setCardItems] = useState<any[]>([])

  const setCardData = (data: any, card_option: string) => {
    let description = ''
    if (data.content?.content && data.content.content[0]?.content?.content[0]?.content[0]?.text) {
      description = data.content.content[0].content.content[0].content[0].text
    }

    if (data?.content && data.content[0]?.content?.content[0]?.content[0]?.text) {
      description = data.content[0].content.content[0].content[0].text
    }

    let asset = data.content.featured_image || null
    if (data.content.card_asset) {
      asset = data.content.card_asset
    }
    if (data.asset) {
      asset = data.asset
    }

    let author = null

    // Handle author data from the article content
    if (data.content.author && card_option !== 'insight' && card_option !== 'tertiary') {
      const authorData = data.content.author
      author = (
        <PhnxAuthorcard
          name={authorData.name}
          layout="compact"
          asset={{
            src: authorData.content?.image?.filename || '',
            alt: authorData.content?.image?.alt || '',
          }}
        />
      )
    }

    let date = null
    if (data.content.date && card_option === 'insight') {
      date = data.content.date
    }

    let title =
      data.content.cardtitle && data.content.cardtitle !== ''
        ? data.content.cardtitle
        : data.content.title

    if (data.title !== undefined) {
      title = data.title
    }

    let subtitle = data.content.location
    if (data.subtitle) subtitle = data.subtitle

    let link = null
    if (data.full_slug) link = '/' + data.full_slug
    if (data.link?.cached_url) link = data.link.cached_url

    let linkLabel = card_option === 'insight' ? 'Read Story' : null
    if (data.linkLabel) linkLabel = data.linkLabel

    return {
      title,
      subtitle,
      place: data.content.place,
      description,
      asset: { src: asset?.filename, alt: asset?.alt, focus: asset?.focus },
      date: date ? setDate(date) : null,
      author,
      tags: data.content.tags,
      timeToRead: ArticleReadTime(data.content.body),
      link,
      buttonLabel: linkLabel,
      articleType: data.content.article_type,
      insightType: data.content.article_type,
      duration: data.content.duration,
    }
  }

  useEffect(() => {
    const itemsAreUUIDs = blok.items && typeof blok.items[0] === 'string'

    if (itemsAreUUIDs && blok.items.length > 0 && rels?.length > 0) {
      const items = blok.items
        .map((uuid: string) => {
          const rel = rels.find((r: { uuid: string }) => r.uuid === uuid)
          if (!rel) return null
          return setCardData(rel, blok.card_option as string)
        })
        .filter(Boolean)

      setCardItems(items)
      return
    }

    if (!itemsAreUUIDs && blok.items?.length > 0) {
      const items = blok.items
        .map((item: any) => setCardData(item, blok.card_option as string))
        .filter(Boolean)
      setCardItems(items)
      return
    }

    if (blok.items && blok.items.length > 0 && (!rels || rels.length === 0)) {
      const fetchItems = async () => {
        const storyblokApi = getStoryblokApi()
        const uuids = blok.items.join(',')
        const sbParams: ISbStoriesParams = {
          ...searchParamsData,
          by_uuids: uuids,
          version: 'published',
          per_page: blok.items.length,
        }

        try {
          const { data } = await storyblokApi.get('cdn/stories', sbParams)
          if (data?.stories) {
            const items = data.stories
              .map((item: any) => setCardData(item, blok.card_option as string))
              .filter(Boolean)
            setCardItems(items)
          }
        } catch (error) {
          console.error('Error fetching stories:', error)
        }
      }
      fetchItems()
    }

    if (blok.manualItems && blok.manualItems.length > 0) {
      const items = blok.manualItems
        .map((item: any) => setCardData(item, blok.card_option as string))
        .filter(Boolean)
      setCardItems(items)
    }

    if (blok.pullItemsByType && blok.pullItemsByType.length > 0) {
      const fetchRelatedArticles = async () => {
        const cats = blok.pullItemsByType.join(',')
        const location = process.env.NEXT_PUBLIC_NEXT_LOCATION || 'au'
        const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

        const sbParams = {
          ...searchParamsData,
          filter_query: {
            component: { in: 'article' },
            __or: [
              { 'settings.location_available': { in: location } },
              { 'settings.location_available': { not_in: filterLocation } },
            ],
            article_type: { in: cats },
          },
          sort_by: 'content.date:desc',
          per_page: 8,
        }

        const storyblokApi = getStoryblokApi()
        const { data } = await storyblokApi.get(`cdn/stories/`, sbParams)
        if (data?.stories) {
          const items = data.stories
            .map((item: any) => setCardData(item, blok.card_option as string))
            .filter(Boolean)
          setCardItems(items)
        }
      }
      fetchRelatedArticles()
    }
  }, [blok.card_option, blok.items, blok.manualItems, blok.pullItemsByType, rels])

  return (
    <div {...storyblokEditable(blok)} className={blok.manualItems ? 'manualCards' : ''}>
      <PhnxRelatedArticles
        title={blok.title}
        subtitle={blok.subtitle}
        description={blok.description?.map((item) => (
          <StoryblokServerComponent blok={item} key={item._uid} />
        ))}
        button={
          blok.button && blok.button.length > 0
            ? blok.button.map((item) => <StoryblokServerComponent blok={item} key={item._uid} />)
            : null
        }
        layout={blok.layout}
        textColor={stylePanel.textColor}
        backgroundColor={stylePanel.backgroundColor}
        padding={stylePanel.padding}
        container={true}
        items={cardItems.map((item, index) => (
          <PhnxCard
            key={index}
            variant={
              blok.layout === 'podcasts'
                ? 'podcast'
                : blok.card_option === 'insight'
                  ? 'senary'
                  : blok.card_option === 'report'
                    ? 'quaternary'
                    : (blok.card_option as
                        | 'primary'
                        | 'secondary'
                        | 'tertiary'
                        | 'quaternary'
                        | 'quinary'
                        | 'senary'
                        | 'septenary')
            }
            layout={blok.card_option === 'report' ? 'horizontal' : 'vertical'}
            alignment={'default'}
            style={'square'}
            title={item.title}
            description={blok.layout === 'podcasts' ? item.duration : item.description || ''}
            tags={blok.card_option === 'report' ? ['Report'] : item.tags}
            author={item.author || null}
            asset={
              item.asset && (
                <PhnxImageHolder
                  images={[{ src: item.asset.src, alt: item.asset.alt, focus: item.asset.focus }]}
                />
              )
            }
            link={item.link}
            subtitle={
              blok.card_option === 'insight' ? (
                <div>
                  <div className="date">{item.date}</div>
                  <div className="timetoread">{item.timeToRead}</div>
                </div>
              ) : (
                item.subtitle
              )
            }
            button={
              item.buttonLabel && (
                <PhnxButton type="button" variant="quinary" label={item.buttonLabel} />
              )
            }
            insightType={item.insightType}
            duration={item.duration}
          />
        ))}
      />
    </div>
  )
}

export default Relatedarticles
